import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
} from '@progress/kendo-react-charts';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { formatPrice } from '../../../../../../common/global/helpers';
import Loader from '../../../../../../common/widgets/Loader';
import { config } from '../../../../config';
import { BudgetTimelineModel } from '../../../../models/CompanyStatistics';
import { StatisticsFilterModel } from '../../../../models/StatisticsFilterModel';
import { resources } from '../../../../resources/resources';
import { companyStatisticsService } from '../../../../services/companyStatistics';
import { EmptyStateAdminDashboardBudgetTimeline } from '../../../Common/EmptyStates';

interface BudgetTimelineState {
  isLoading: boolean;
  timeline?: BudgetTimelineModel;
}

interface BudgetTimelineProps {
  filters: StatisticsFilterModel;
}

export const BudgetTimeline = (props: Readonly<BudgetTimelineProps>) => {
  const { t: translate } = useTranslation();
  const [state, setState] = useState<BudgetTimelineState>({
    isLoading: true,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadBudgetTimeline = async () => {
      const response = await companyStatisticsService.getBudgetTimeline(
        props.filters,
        {
          signal: controller?.signal,
        },
      );

      if (controller.signal.aborted) return;

      if (!response.hasErrors) {
        setState(
          produce(draft => {
            draft.timeline = response.data;
          }),
        );
      }

      setState(
        produce(draft => {
          draft.isLoading = false;
        }),
      );
    };

    loadBudgetTimeline();

    return () => {
      controller.abort();
    };
  }, [props.filters]);

  return (
    <div className="panel padding-xl admin-statistics-section">
      <h2 className="statistics-subtitle">
        {translate(resources.admin.statistics.budgetTimeline)}
      </h2>

      <Loader loading={state.isLoading} loader={<Skeleton />}>
        {state.timeline ? (
          <div className="">
            <Chart>
              <ChartTooltip render={valueTooltipRender} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem majorGridLines={{ visible: false }} />
              </ChartCategoryAxis>
              <ChartSeries>
                <ChartSeriesItem
                  type="column"
                  tooltip={{
                    visible: true,
                  }}
                  color="#7FD0D6"
                  name={translate(
                    resources.admin.statistics
                      .budgetTimelineTotalUploadedAmount,
                  )}
                  data={[state.timeline.addedAmount]}
                />
                <ChartSeriesItem
                  type="column"
                  tooltip={{
                    visible: true,
                  }}
                  color="#FF220D"
                  name={translate(
                    resources.admin.statistics
                      .budgetTimelineTotalConsumedAmount,
                  )}
                  data={[state.timeline.availableAmount]}
                />
              </ChartSeries>
              <ChartLegend visible={true} position="bottom" />
            </Chart>
          </div>
        ) : (
          <EmptyStateAdminDashboardBudgetTimeline />
        )}
      </Loader>
    </div>
  );
};

const valueTooltipRender = (props: any) => {
  return (
    <span>
      <b>
        {formatPrice(props.point?.value)} {config.currency}
      </b>
    </span>
  );
};
